import React, { useState } from 'react';
import AdminSidebar from '../../components/ui/AdminSidebar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const ModuleAnalyticsDashboard = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [timePeriod, setTimePeriod] = useState('month');
  const [selectedModule, setSelectedModule] = useState('all');

  const moduleUsageData = [
    {
      id: 1,
      moduleName: 'Predictive Analysis',
      totalUsers: 847,
      activeUsers: 623,
      avgSessionTime: '12m 34s',
      totalSessions: 4521,
      featureAdoption: 78,
      trend: 'up',
      trendValue: 15.3,
      topFeatures: ['Revenue Forecasting', 'Risk Analysis', 'AI Suggestions'],
      userSatisfaction: 4.6,
      errorRate: 0.8
    },
    {
      id: 2,
      moduleName: 'Business Model Canvas',
      totalUsers: 692,
      activeUsers: 521,
      avgSessionTime: '18m 45s',
      totalSessions: 3845,
      featureAdoption: 85,
      trend: 'up',
      trendValue: 22.1,
      topFeatures: ['Canvas Builder', 'AI Suggestions', 'Export PDF'],
      userSatisfaction: 4.8,
      errorRate: 0.3
    },
    {
      id: 3,
      moduleName: 'Goal Intelligence',
      totalUsers: 534,
      activeUsers: 412,
      avgSessionTime: '9m 12s',
      totalSessions: 2967,
      featureAdoption: 72,
      trend: 'up',
      trendValue: 8.7,
      topFeatures: ['Goal Setting', 'Milestone Tracking', 'Gap Analysis'],
      userSatisfaction: 4.5,
      errorRate: 1.2
    },
    {
      id: 4,
      moduleName: 'Reports Dashboard',
      totalUsers: 789,
      activeUsers: 645,
      avgSessionTime: '7m 23s',
      totalSessions: 5234,
      featureAdoption: 91,
      trend: 'up',
      trendValue: 12.4,
      topFeatures: ['Executive Summary', 'Export Reports', 'Historical Analysis'],
      userSatisfaction: 4.7,
      errorRate: 0.5
    },
    {
      id: 5,
      moduleName: 'Billing Management',
      totalUsers: 892,
      activeUsers: 734,
      avgSessionTime: '5m 48s',
      totalSessions: 6123,
      featureAdoption: 95,
      trend: 'neutral',
      trendValue: 2.1,
      topFeatures: ['Payment History', 'Upgrade Plans', 'Invoice Download'],
      userSatisfaction: 4.4,
      errorRate: 1.5
    },
    {
      id: 6,
      moduleName: 'Dashboard',
      totalUsers: 1247,
      activeUsers: 1089,
      avgSessionTime: '4m 15s',
      totalSessions: 8934,
      featureAdoption: 98,
      trend: 'up',
      trendValue: 5.6,
      topFeatures: ['Metric Cards', 'Revenue Chart', 'Quick Actions'],
      userSatisfaction: 4.6,
      errorRate: 0.4
    }
  ];

  const featureAdoptionTrends = [
    { month: 'Aug', predictive: 65, canvas: 72, goals: 58, reports: 85, billing: 92, dashboard: 96 },
    { month: 'Sep', predictive: 68, canvas: 76, goals: 62, reports: 87, billing: 93, dashboard: 97 },
    { month: 'Oct', predictive: 71, canvas: 79, goals: 66, reports: 88, billing: 94, dashboard: 97 },
    { month: 'Nov', predictive: 74, canvas: 82, goals: 69, reports: 90, billing: 94, dashboard: 98 },
    { month: 'Dec', predictive: 76, canvas: 84, goals: 71, reports: 90, billing: 95, dashboard: 98 },
    { month: 'Jan', predictive: 78, canvas: 85, goals: 72, reports: 91, billing: 95, dashboard: 98 }
  ];

  const performanceMetrics = [
    { metric: 'Avg Load Time', value: '1.2s', status: 'good', target: '< 2s' },
    { metric: 'API Response Time', value: '245ms', status: 'good', target: '< 500ms' },
    { metric: 'Error Rate', value: '0.7%', status: 'good', target: '< 1%' },
    { metric: 'Uptime', value: '99.8%', status: 'excellent', target: '> 99.5%' },
    { metric: 'User Satisfaction', value: '4.6/5', status: 'good', target: '> 4.0' },
    { metric: 'Feature Completion', value: '84%', status: 'good', target: '> 80%' }
  ];

  const getTrendIcon = (trend) => {
    if (trend === 'up') return 'TrendingUp';
    if (trend === 'down') return 'TrendingDown';
    return 'Minus';
  };

  const getTrendColor = (trend) => {
    if (trend === 'up') return 'text-emerald-600';
    if (trend === 'down') return 'text-red-600';
    return 'text-slate-600';
  };

  const getStatusColor = (status) => {
    if (status === 'excellent') return 'bg-emerald-100 text-emerald-700';
    if (status === 'good') return 'bg-blue-100 text-blue-700';
    if (status === 'warning') return 'bg-amber-100 text-amber-700';
    return 'bg-red-100 text-red-700';
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      <AdminSidebar isCollapsed={sidebarCollapsed} />

      <div className={`flex-1 transition-all duration-300 ${sidebarCollapsed ? 'ml-20' : 'ml-64'}`}>
        {/* Header */}
        <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
          <div className="px-8 py-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-3xl font-bold text-slate-900">Module Analytics</h1>
                <p className="text-slate-600 mt-1">Track feature usage, adoption rates, and performance metrics</p>
              </div>
              <div className="flex items-center gap-3">
                <select
                  value={timePeriod}
                  onChange={(e) => setTimePeriod(e?.target?.value)}
                  className="px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                >
                  <option value="week">Last 7 Days</option>
                  <option value="month">Last 30 Days</option>
                  <option value="quarter">Last Quarter</option>
                  <option value="year">Last Year</option>
                </select>
                <Button
                  onClick={() => console.log('Export analytics')}
                  className="bg-violet-600 hover:bg-violet-700 text-white px-6 py-2.5 rounded-lg font-medium flex items-center gap-2"
                >
                  <Icon name="Download" className="w-5 h-5" />
                  Export Report
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="p-8">
          {/* Overview Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-slate-200">
              <div className="flex items-center justify-between mb-2">
                <span className="text-slate-600 text-sm font-medium">Total Active Users</span>
                <Icon name="Users" className="w-5 h-5 text-blue-600" />
              </div>
              <div className="text-3xl font-bold text-slate-900">1,247</div>
              <div className="flex items-center gap-1 mt-2 text-emerald-600 text-sm">
                <Icon name="TrendingUp" className="w-4 h-4" />
                <span>+12.5% vs last period</span>
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 border border-slate-200">
              <div className="flex items-center justify-between mb-2">
                <span className="text-slate-600 text-sm font-medium">Avg Session Time</span>
                <Icon name="Clock" className="w-5 h-5 text-violet-600" />
              </div>
              <div className="text-3xl font-bold text-slate-900">9m 42s</div>
              <div className="flex items-center gap-1 mt-2 text-emerald-600 text-sm">
                <Icon name="TrendingUp" className="w-4 h-4" />
                <span>+8.3% vs last period</span>
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 border border-slate-200">
              <div className="flex items-center justify-between mb-2">
                <span className="text-slate-600 text-sm font-medium">Feature Adoption</span>
                <Icon name="Target" className="w-5 h-5 text-emerald-600" />
              </div>
              <div className="text-3xl font-bold text-slate-900">86.5%</div>
              <div className="flex items-center gap-1 mt-2 text-emerald-600 text-sm">
                <Icon name="TrendingUp" className="w-4 h-4" />
                <span>+15.7% vs last period</span>
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 border border-slate-200">
              <div className="flex items-center justify-between mb-2">
                <span className="text-slate-600 text-sm font-medium">User Satisfaction</span>
                <Icon name="Star" className="w-5 h-5 text-amber-500" />
              </div>
              <div className="text-3xl font-bold text-slate-900">4.6/5</div>
              <div className="flex items-center gap-1 mt-2 text-emerald-600 text-sm">
                <Icon name="TrendingUp" className="w-4 h-4" />
                <span>+0.3 vs last period</span>
              </div>
            </div>
          </div>

          {/* Feature Adoption Trends Chart */}
          <div className="bg-white rounded-xl border border-slate-200 p-6 mb-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-bold text-slate-900">Feature Adoption Trends</h2>
                <p className="text-slate-600 text-sm mt-1">Monthly adoption rates across all modules</p>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                  <span className="text-sm text-slate-600">Predictive</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-violet-500"></div>
                  <span className="text-sm text-slate-600">Canvas</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
                  <span className="text-sm text-slate-600">Goals</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-amber-500"></div>
                  <span className="text-sm text-slate-600">Reports</span>
                </div>
              </div>
            </div>
            <div className="h-80 flex items-end justify-between gap-4">
              {featureAdoptionTrends?.map((data, index) => (
                <div key={index} className="flex-1 flex flex-col items-center">
                  <div className="w-full flex flex-col gap-1 mb-2">
                    <div className="bg-blue-500 rounded-t" style={{ height: `${data?.predictive * 2}px` }}></div>
                    <div className="bg-violet-500" style={{ height: `${data?.canvas * 2}px` }}></div>
                    <div className="bg-emerald-500" style={{ height: `${data?.goals * 2}px` }}></div>
                    <div className="bg-amber-500 rounded-b" style={{ height: `${data?.reports * 2}px` }}></div>
                  </div>
                  <span className="text-sm text-slate-600 font-medium">{data?.month}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Module Usage Details */}
          <div className="bg-white rounded-xl border border-slate-200 mb-8">
            <div className="px-6 py-4 border-b border-slate-200">
              <h2 className="text-xl font-bold text-slate-900">Module Usage Details</h2>
              <p className="text-slate-600 text-sm mt-1">Comprehensive analytics for each module</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Module Name</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Total Users</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Active Users</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Avg Session</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Adoption Rate</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Satisfaction</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Trend</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {moduleUsageData?.map((module) => (
                    <tr key={module?.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="font-medium text-slate-900">{module?.moduleName}</div>
                        <div className="text-sm text-slate-500 mt-1">
                          Top: {module?.topFeatures?.slice(0, 2)?.join(', ')}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-slate-700 font-medium">{module?.totalUsers?.toLocaleString()}</td>
                      <td className="px-6 py-4">
                        <div className="text-slate-700 font-medium">{module?.activeUsers?.toLocaleString()}</div>
                        <div className="text-sm text-slate-500">
                          {Math.round((module?.activeUsers / module?.totalUsers) * 100)}% active
                        </div>
                      </td>
                      <td className="px-6 py-4 text-slate-700">{module?.avgSessionTime}</td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 bg-slate-200 rounded-full h-2 w-24">
                            <div
                              className="bg-violet-600 h-2 rounded-full"
                              style={{ width: `${module?.featureAdoption}%` }}
                            ></div>
                          </div>
                          <span className="text-sm font-medium text-slate-700">{module?.featureAdoption}%</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-1">
                          <Icon name="Star" className="w-4 h-4 text-amber-500 fill-amber-500" />
                          <span className="text-slate-700 font-medium">{module?.userSatisfaction}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className={`flex items-center gap-1 ${getTrendColor(module?.trend)}`}>
                          <Icon name={getTrendIcon(module?.trend)} className="w-4 h-4" />
                          <span className="font-medium">{module?.trendValue}%</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Performance Metrics */}
          <div className="bg-white rounded-xl border border-slate-200">
            <div className="px-6 py-4 border-b border-slate-200">
              <h2 className="text-xl font-bold text-slate-900">Performance Metrics</h2>
              <p className="text-slate-600 text-sm mt-1">System performance and reliability indicators</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 p-6">
              {performanceMetrics?.map((metric, index) => (
                <div key={index} className="border border-slate-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-slate-600 text-sm font-medium">{metric?.metric}</span>
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(metric?.status)}`}>
                      {metric?.status}
                    </span>
                  </div>
                  <div className="text-2xl font-bold text-slate-900 mb-2">{metric?.value}</div>
                  <div className="text-sm text-slate-500">Target: {metric?.target}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModuleAnalyticsDashboard;